import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiRequest } from "../../../network/apiClient";
import { API_ENDPOINTS } from "../../../api/apiEndpoints";
import { toast } from "react-toastify";

interface EditReferrerButtonProps {
  id: number | string;
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
  refetch: () => void;
}

interface FormData {
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
  id: number | string;
}

const EditReferrerButton = ({ id, name, lead_source, mobile_no, email, refetch }: EditReferrerButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    name: name || "",
    lead_source: lead_source || "",
    mobile_no: mobile_no || "",
    email: email || "",
    id: id || "",
  });

  // Sync formData if props change
  useEffect(() => {
    setFormData({
      name: name || "",
      lead_source: lead_source || "",
      mobile_no: mobile_no || "",
      email: email || "",
      id: id || "",
    });
  }, [id, name, lead_source, mobile_no, email]);

  const leadSourceOptions: string[] = [
    "Referrer MM Employee",
    "Referrer Margin Sharing",
  ];

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.mobile_no) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsLoading(true);
    try {
      await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.SETTINGS.EDIT_REFERRER}/${id}`,
        "PUT",
        {
          name: formData.name,
          lead_source: formData.lead_source,
          mobile_no: formData.mobile_no,
          email: formData.email,
        }
      );
      if (refetch) refetch();
      toast.success("Referrer Successfully Updated");
      setIsModalOpen(false);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to update referrer");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="text-[#1E293B] block px-4 py-2 text-sm hover:bg-[#F1F5F9] text-left w-full"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="inline-block w-5 h-5 mr-2">
          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
        </svg>
        Edit Referrer
      </button>

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
            <div className="bg-white p-6 rounded-t-2xl md:rounded-lg w-full md:w-1/3 max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gray-300 mb-6">
                <h2 className="text-xl font-bold mb-2">Edit Referrer</h2>
              </div>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter referrer name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Lead Source</label>
                  <select
                    name="lead_source"
                    value={formData.lead_source}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="" disabled>Select a lead source</option>
                    {leadSourceOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mobile_no"
                    value={formData.mobile_no}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter email address"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-3 bg-white border-[1px] border-black rounded-3xl hover:bg-gray-100"
                  >
                    <b>Cancel</b>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className={`rounded-3xl px-8 py-3 text-black ${
                      isLoading ? "bg-gray-500" : "bg-yellow-300 hover:bg-yellow-400"
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : <b>Save</b>}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default EditReferrerButton;